import React, { useState, useEffect } from 'react';
import { getGeneralInsuranceList } from '../../model/api';
import { toast } from 'react-toastify';

export const GIPoliciesCommissionSubTab = () => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [summary, setSummary] = useState({
        totalPolicies: 0,
        totalPolicyAmount: 0,
        totalCommission: 0,
        paidCommission: 0,
        unpaidCommission: 0
    });

    useEffect(() => {
        fetchPolicies();
    }, [selectedMonth]);

    const fetchPolicies = async () => {
        setLoading(true);
        try {
            const params = { status: 'approved' };
            if (selectedMonth) params.month = selectedMonth;

            const result = await getGeneralInsuranceList(params);
            if (result.ok) {
                const approvedPolicies = result.data.applications || [];
                setPolicies(approvedPolicies);
                calculateSummary(approvedPolicies);
            }
        } catch (error) {
            console.error('Error fetching policies:', error);
            toast.error('Failed to fetch policies');
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = (policyList) => {
        const summary = policyList.reduce((acc, policy) => {
            const payout = policy.user_payout;
            return {
                totalPolicies: acc.totalPolicies + 1,
                totalPolicyAmount: acc.totalPolicyAmount + (policy.policy_amount || 0),
                totalCommission: acc.totalCommission + (payout?.payout_amount || 0),
                paidCommission: acc.paidCommission + (payout?.status === 'paid' ? (payout.payout_amount || 0) : 0),
                unpaidCommission: acc.unpaidCommission + (payout?.status === 'unpaid' ? (payout.payout_amount || 0) : 0)
            };
        }, {
            totalPolicies: 0,
            totalPolicyAmount: 0,
            totalCommission: 0,
            paidCommission: 0,
            unpaidCommission: 0
        });
        setSummary(summary);
    };



    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner"></div>
                <p>Loading policies...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '30px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
                    My Policies & Commission
                </h2>

                {/* Month Filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginRight: '10px', fontWeight: '500' }}>Filter by Month:</label>
                    {/* Month Select */}
                    <select
                        value={selectedMonth ? selectedMonth.split('-')[1] : ''}
                        onChange={(e) => {
                            const y = selectedMonth ? selectedMonth.split('-')[0] : new Date().getFullYear();
                            const m = e.target.value;
                            if (m === '') setSelectedMonth(''); // Clear if "All Time" selected
                            else setSelectedMonth(`${y}-${m}`);
                        }}
                        style={{
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            marginRight: '10px'
                        }}
                    >
                        <option value="">All Time</option>
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                    </select>

                    {/* Year Select */}
                    <select
                        value={selectedMonth ? selectedMonth.split('-')[0] : ''}
                        onChange={(e) => {
                            const m = selectedMonth ? selectedMonth.split('-')[1] : new Date().getMonth() + 1;
                            const monthStr = String(m).padStart(2, '0');
                            const y = e.target.value;
                            if (y === '') setSelectedMonth('');
                            else setSelectedMonth(`${y}-${monthStr}`);
                        }}
                        style={{
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    >
                        <option value="">All Years</option>
                        {Array.from({ length: 11 }, (_, i) => 2025 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                    {selectedMonth && (
                        <button
                            onClick={() => setSelectedMonth('')}
                            style={{
                                marginLeft: '10px',
                                padding: '8px 16px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Clear Filter
                        </button>
                    )}
                </div>

                {/* Summary Cards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px',
                    marginBottom: '30px'
                }}>
                    <div style={{
                        padding: '20px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        border: '1px solid #2196f3'
                    }}>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Policies</p>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>
                            {summary.totalPolicies}
                        </p>
                    </div>

                    <div style={{
                        padding: '20px',
                        backgroundColor: '#f3e5f5',
                        borderRadius: '8px',
                        border: '1px solid #9c27b0'
                    }}>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Policy Amount</p>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#7b1fa2' }}>
                            ₹{summary.totalPolicyAmount.toLocaleString()}
                        </p>
                    </div>

                    <div style={{
                        padding: '20px',
                        backgroundColor: '#e8f5e9',
                        borderRadius: '8px',
                        border: '1px solid #4caf50'
                    }}>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Commission</p>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#388e3c' }}>
                            ₹{summary.totalCommission.toFixed(2)}
                        </p>
                    </div>

                    <div style={{
                        padding: '20px',
                        backgroundColor: '#fff3e0',
                        borderRadius: '8px',
                        border: '1px solid #ff9800'
                    }}>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Paid / Unpaid</p>
                        <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#f57c00' }}>
                            ₹{summary.paidCommission.toFixed(2)} / ₹{summary.unpaidCommission.toFixed(2)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Policies Table */}
            {policies.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    <p style={{ fontSize: '16px', color: '#666' }}>No approved policies found</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                                    Application ID
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                                    Customer
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                                    Type
                                </th>
                                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                                    Policy Amount
                                </th>
                                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                                    My Commission
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                                    Payment Status
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                                    Approved Date
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {policies.map((policy, index) => (
                                <tr
                                    key={policy._id}
                                    style={{
                                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                        borderBottom: '1px solid #eee'
                                    }}
                                >
                                    <td style={{ padding: '12px' }}>
                                        <span style={{ fontWeight: '500', color: '#1976d2' }}>
                                            {policy.application_id}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <div>
                                            <div style={{ fontWeight: '500' }}>{policy.customer_name}</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                {policy.customer_phone}
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{
                                            padding: '4px 8px',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            textTransform: 'capitalize'
                                        }}>
                                            {policy.insurance_type}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '500' }}>
                                        ₹{policy.policy_amount?.toLocaleString()}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                                        <span style={{ fontWeight: 'bold', color: '#388e3c', fontSize: '16px' }}>
                                            ₹{policy.user_payout?.payout_amount?.toFixed(2) || '0.00'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <span style={{
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            backgroundColor: policy.user_payout?.status === 'paid' ? '#d1fae5' : '#fef3c7',
                                            color: policy.user_payout?.status === 'paid' ? '#065f46' : '#92400e'
                                        }}>
                                            {policy.user_payout?.status === 'paid' ? '✓ Paid' : 'Unpaid'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>
                                        {new Date(policy.approved_at || policy.createdAt).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Month-wise Breakdown */}
            {policies.length > 0 && (
                <div style={{ marginTop: '40px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
                        Month-wise Breakdown
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                                        Month
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                                        Policies
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                                        Total Policy Amount
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                                        Total Commission
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(
                                    policies.reduce((acc, policy) => {
                                        const monthKey = new Date(policy.approved_at || policy.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                        if (!acc[monthKey]) {
                                            acc[monthKey] = {
                                                policies: 0,
                                                totalAmount: 0,
                                                totalCommission: 0
                                            };
                                        }
                                        acc[monthKey].policies += 1;
                                        acc[monthKey].totalAmount += policy.policy_amount || 0;
                                        acc[monthKey].totalCommission += policy.user_payout?.payout_amount || 0;
                                        return acc;
                                    }, {})
                                ).sort((a, b) => new Date(b[0]) - new Date(a[0])).map(([month, data], index) => (
                                    <tr key={month} style={{
                                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                        borderBottom: '1px solid #eee'
                                    }}>
                                        <td style={{ padding: '12px', fontWeight: '500' }}>
                                            {month}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'right' }}>
                                            {data.policies}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '500' }}>
                                            ₹{data.totalAmount.toLocaleString()}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#388e3c' }}>
                                            ₹{data.totalCommission.toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GIPoliciesCommissionSubTab;
